import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  IconArrowRight,
  IconBrain,
  IconBrandGithub,
  IconBrowserCheck,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconCode,
  IconMenu2,
  IconMoon,
  IconNetwork,
  IconPackage,
  IconPhoto,
  IconPlugConnected,
  IconShieldCheck,
  IconSparkles,
  IconSun,
  IconTerminal2,
  IconX,
} from '@tabler/icons-react';
import { useAuth, useTheme } from '../state';
import { Button } from '../components/ui';

export function LandingPage(): JSX.Element {
  const { apiKey } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="landing">
      {/* ------------------------------------------------ Atmospheric Canvas */}
      <div className="landing__atmosphere" aria-hidden="true">
        <div className="landing__glow-orb-1" />
        <div className="landing__glow-orb-2" />
        <div className="landing__glow-orb-3" />
        <div className="landing__grid-pattern" />
      </div>

      {/* ------------------------------------------------ Fluid Island Navigation */}
      <header className="landing__nav">
        <div className="landing__nav-inner">
          <Link to="/" className="landing__logo">
            <span className="landing__logo-mark">J</span>
            <span className="landing__logo-text">Justloop</span>
            <span className="landing__badge ml-2 hidden sm:inline-flex">
              Apache 2.0
            </span>
          </Link>

          <nav className="landing__links" aria-label="Main Navigation">
            <a href="#features" className="landing__nav-link">Features</a>
            <a href="#comparison" className="landing__nav-link">vs TestSprite</a>
            <a href="#how-it-works" className="landing__nav-link">How It Works</a>
            <a href="#pricing" className="landing__nav-link">Pricing</a>
            <a href="#faq" className="landing__nav-link">FAQ</a>
            <Link to="/docs" className="landing__nav-link">CLI &amp; Docs</Link>
          </nav>

          <div className="landing__nav-actions">
            <button
              type="button"
              onClick={toggle}
              title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              aria-label="Toggle color theme"
              className="w-8 h-8 rounded-full border border-white/10 dark:border-white/10 bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white flex items-center justify-center transition-all duration-300"
            >
              {theme === 'dark' ? (
                <IconSun className="w-3.5 h-3.5 text-amber-400" stroke={1.5} />
              ) : (
                <IconMoon className="w-3.5 h-3.5 text-slate-700" stroke={1.5} />
              )}
            </button>

            {apiKey ? (
              <Button
                size="sm"
                onClick={() => navigate('/')}
                className="btn-island-primary !py-1 !pl-3.5 !pr-1 text-xs"
              >
                <span>Dashboard</span>
                <span className="btn-island-icon !w-6 !h-6">
                  <IconArrowRight className="w-3 h-3" stroke={2} />
                </span>
              </Button>
            ) : (
              <>
                <Link
                  to="/login"
                  className="hidden sm:inline-flex text-xs font-semibold text-slate-300 hover:text-white px-3 py-1.5 transition-colors"
                >
                  Sign In
                </Link>
                <Link
                  to="/login"
                  className="btn-island-primary !py-1 !pl-3.5 !pr-1 text-xs"
                >
                  <span>Get Started Free</span>
                  <span className="btn-island-icon !w-6 !h-6">
                    <IconArrowRight className="w-3 h-3" stroke={2} />
                  </span>
                </Link>
              </>
            )}

            {/* Mobile Hamburger Morph Button */}
            <button
              type="button"
              className="md:hidden w-8 h-8 rounded-full border border-white/10 bg-white/5 flex items-center justify-center text-slate-300 hover:text-white transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? (
                <IconX className="w-4 h-4" stroke={1.5} />
              ) : (
                <IconMenu2 className="w-4 h-4" stroke={1.5} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Modal Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-3 max-w-sm mx-auto p-4 rounded-3xl bg-[#090d16]/95 border border-white/10 backdrop-blur-2xl shadow-2xl flex flex-col gap-3 text-sm pointer-events-auto">
            <a
              href="#features"
              onClick={() => setMobileMenuOpen(false)}
              className="px-4 py-2 rounded-xl text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
            >
              Features
            </a>
            <a
              href="#comparison"
              onClick={() => setMobileMenuOpen(false)}
              className="px-4 py-2 rounded-xl text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
            >
              vs TestSprite
            </a>
            <a
              href="#how-it-works"
              onClick={() => setMobileMenuOpen(false)}
              className="px-4 py-2 rounded-xl text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
            >
              How It Works
            </a>
            <a
              href="#pricing"
              onClick={() => setMobileMenuOpen(false)}
              className="px-4 py-2 rounded-xl text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
            >
              Pricing
            </a>
            <a
              href="#faq"
              onClick={() => setMobileMenuOpen(false)}
              className="px-4 py-2 rounded-xl text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
            >
              FAQ
            </a>
            <Link
              to="/docs"
              onClick={() => setMobileMenuOpen(false)}
              className="px-4 py-2 rounded-xl text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
            >
              CLI &amp; Setup Docs
            </Link>
            <div className="pt-2 border-t border-white/10 flex items-center justify-between">
              <Link
                to="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="w-full text-center py-2.5 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-medium text-xs shadow-lg"
              >
                Sign In to Dashboard
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* ------------------------------------------------ Hero Section */}
      <section className="landing__hero min-h-[92dvh] pt-20 md:pt-28 pb-16 md:pb-24 flex flex-col justify-center max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14 items-center">
          {/* Left Column: Asymmetric Value Proposition */}
          <div className="lg:col-span-7 flex flex-col items-start text-left">
            <div className="inline-flex items-center gap-2.5 rounded-full border border-blue-500/30 bg-blue-500/10 px-3.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-blue-300 mb-6 backdrop-blur-md shadow-[0_0_20px_rgba(59,130,246,0.15)]">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              <span>The Verification Layer for Agentic Coding</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-[4.15rem] font-bold tracking-[-0.035em] leading-[1.05] text-white mb-6">
              Your agent ships in minutes.<br />
              <span className="landing__headline-gradient">Verification must keep up.</span>
            </h1>

            <p className="text-base sm:text-lg text-slate-300/90 leading-relaxed max-w-[52ch] mb-8 font-normal">
              Run real products in Chromium, hand coding agents self-consistent failure bundles,
              and bank passing runs into regression suites. Zero mock simulations.
            </p>

            <div className="flex flex-wrap items-center gap-4">
              <Link to="/login" className="btn-island-primary group">
                <span>Start Free with 150 Credits</span>
                <span className="btn-island-icon">
                  <IconArrowRight className="w-4 h-4" stroke={2} />
                </span>
              </Link>
              <a href="#how-it-works" className="btn-island-secondary">
                <span>Explore the Loop</span>
                <IconSparkles className="w-4 h-4 text-blue-400" stroke={1.5} />
              </a>
            </div>

            {/* Micro-line proof badges */}
            <div className="mt-8 flex flex-wrap items-center gap-4 text-xs text-slate-400">
              <span className="flex items-center gap-1.5">
                <IconCheck className="w-3.5 h-3.5 text-emerald-400" stroke={2} /> Apache-2.0 Open Source
              </span>
              <span className="w-1 h-1 rounded-full bg-slate-600" />
              <span className="flex items-center gap-1.5">
                <IconCheck className="w-3.5 h-3.5 text-emerald-400" stroke={2} /> 1-Command Self-Host
              </span>
              <span className="w-1 h-1 rounded-full bg-slate-600" />
              <span className="flex items-center gap-1.5">
                <IconCheck className="w-3.5 h-3.5 text-emerald-400" stroke={2} /> Native MCP (9 Tools)
              </span>
            </div>
          </div>

          {/* Right Column: Machined Double-Bezel Live Failure Bundle Preview */}
          <div className="lg:col-span-5 w-full">
            <div className="double-bezel">
              <div className="double-bezel__inner">
                {/* Hardware Window Bar */}
                <div className="p-3.5 px-4.5 border-b border-white/[0.08] flex items-center justify-between bg-white/[0.02]">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-rose-500/80" />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-500/80" />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
                    <span className="ml-2 font-mono text-[11px] text-slate-400">forensic_bundle.json</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] text-rose-400 bg-rose-500/10 border border-rose-500/20 px-2 py-0.5 rounded-full font-semibold">
                      ASSERTION FAILED
                    </span>
                    <span className="font-mono text-[10px] text-slate-400">step 3/3</span>
                  </div>
                </div>

                {/* Bundle Card Body */}
                <div className="p-5 font-mono text-xs space-y-4">
                  <div className="space-y-1.5">
                    <div className="text-slate-400 uppercase text-[10px] tracking-wider font-semibold">
                      Target Assertion
                    </div>
                    <div className="text-blue-300 bg-white/[0.03] p-3 rounded-xl border border-white/[0.08] font-mono text-xs leading-relaxed">
                      expect(page.getByRole(&apos;status&apos;)).toHaveText(&apos;Order Confirmed&apos;)
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <div className="text-rose-400 font-semibold uppercase text-[10px] tracking-wider">
                      Forensic Diagnosis (Single Snapshot)
                    </div>
                    <div className="bg-rose-500/[0.08] border border-rose-500/20 p-3 rounded-xl space-y-1">
                      <p className="font-semibold text-rose-400">Text mismatch at locator:</p>
                      <p className="text-slate-400">Expected: &apos;Order Confirmed&apos;</p>
                      <p className="text-rose-300 font-semibold">Received: &apos;Payment Declined (code 402)&apos;</p>
                    </div>
                  </div>

                  {/* Bundled Artifacts */}
                  <div className="pt-2 border-t border-white/[0.06] flex items-center justify-between text-[11px] text-slate-400">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 bg-white/[0.04] px-2 py-0.5 rounded-md border border-white/[0.06]">
                        <IconPhoto className="w-3.5 h-3.5 text-blue-400" stroke={1.5} /> shot.png
                      </span>
                      <span className="inline-flex items-center gap-1 bg-white/[0.04] px-2 py-0.5 rounded-md border border-white/[0.06]">
                        <IconCode className="w-3.5 h-3.5 text-blue-400" stroke={1.5} /> dom.html
                      </span>
                    </div>
                    <span className="text-emerald-400 font-semibold flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      Ready for Agent Fix
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------ Monospace Loop Strip Double-Bezel */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 mb-16">
        <div className="double-bezel">
          <div className="double-bezel__inner py-6 px-6 sm:px-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="landing__loop-strip">
              <span className="landing__loop-step">create</span>
              <span className="landing__loop-arrow">→</span>
              <span className="landing__loop-step">run</span>
              <span className="landing__loop-arrow">→</span>
              <span className="landing__loop-step">failure bundle</span>
              <span className="landing__loop-arrow">→</span>
              <span className="landing__loop-step">agent fix</span>
              <span className="landing__loop-arrow">→</span>
              <span className="landing__loop-step">rerun &amp; bank</span>
            </div>

            <p className="text-xs text-slate-400 max-w-sm text-center md:text-right m-0 leading-relaxed">
              Every passing run is banked into a durable regression suite, so coverage compounds
              as your product grows instead of vanishing when the coding context window closes.
            </p>
          </div>
        </div>
      </div>

      {/* ------------------------------------------------- Head-to-Head Comparison */}
      <section id="comparison" className="landing__section">
        <div className="landing__section-header">
          <div className="landing__tag">WHY JUSTLOOP WINS</div>
          <h2>Head-to-Head: Justloop vs TestSprite</h2>
          <p className="muted">
            Test automation tools that rely on AI agents inside the run path create flaky, non-deterministic runs.
            Justloop is built for engineers who demand reproducible evidence.
          </p>
        </div>

        <div className="double-bezel">
          <div className="double-bezel__inner">
            <div className="landing__table-wrap !border-0 !shadow-none">
              <table className="landing__table">
                <thead>
                  <tr>
                    <th style={{ width: '28%' }}>Dimension</th>
                    <th style={{ width: '36%' }} className="landing__col-highlight">
                      Justloop
                    </th>
                    <th style={{ width: '36%' }}>TestSprite &amp; Generic AI Testers</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Test Execution Engine</strong></td>
                    <td className="landing__col-highlight">
                      <span className="landing__check">
                        <IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} />
                        100% Deterministic &amp; Heuristic
                      </span>
                      <div className="landing__table-desc">
                        Zero LLMs in the execution path. Identical plan always executes identical steps with strict Playwright locators.
                      </div>
                    </td>
                    <td>
                      <span className="landing__cross">
                        <IconX className="w-4 h-4 text-rose-500 inline mr-1" stroke={2} />
                        LLM Agent in Run Path
                      </span>
                      <div className="landing__table-desc">
                        AI agents interpret pages on the fly, producing flaky, non-deterministic results that can hallucinate success.
                      </div>
                    </td>
                  </tr>

                  <tr>
                    <td><strong>Auto-Heal Integrity</strong></td>
                    <td className="landing__col-highlight">
                      <span className="landing__check">
                        <IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} />
                        Refuses Rubber-Stamping
                      </span>
                      <div className="landing__table-desc">
                        Auto-heal repairs stale CSS/test-id locators, but <strong>strictly declines to rewrite failing assertions</strong>.
                      </div>
                    </td>
                    <td>
                      <span className="landing__cross">
                        <IconX className="w-4 h-4 text-rose-500 inline mr-1" stroke={2} />
                        Rewrites Broken Tests
                      </span>
                      <div className="landing__table-desc">
                        Silently updates expected text to match broken product pages, turning failed assertions into false-green rubber stamps.
                      </div>
                    </td>
                  </tr>

                  <tr>
                    <td><strong>Pricing &amp; Cost</strong></td>
                    <td className="landing__col-highlight">
                      <span className="landing__check">
                        <IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} />
                        Usage-based pricing is planned
                      </span>
                      <div className="landing__table-desc">
                        Indicative rates only; paid billing is unavailable and the final prices are not confirmed. There is no verified comparison against other products.
                      </div>
                    </td>
                    <td>
                      <span className="landing__cross">
                        <IconX className="w-4 h-4 text-rose-500 inline mr-1" stroke={2} />
                        Seat-based subscriptions
                      </span>
                      <div className="landing__table-desc">
                        Comparison details are omitted until supported by current, dated public sources.
                      </div>
                    </td>
                  </tr>

                  <tr>
                    <td><strong>Model Flexibility (BYOK)</strong></td>
                    <td className="landing__col-highlight">
                      <span className="landing__check">
                        <IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} />
                        True BYOK &amp; Super Admin
                      </span>
                      <div className="landing__table-desc">
                        Bring your own keys (OpenAI, Anthropic, Gemini, local models) or use operator hosted model configured in Super Admin.
                      </div>
                    </td>
                    <td>
                      <span className="landing__cross">
                        <IconX className="w-4 h-4 text-rose-500 inline mr-1" stroke={2} />
                        Closed Vendor Lock-In
                      </span>
                      <div className="landing__table-desc">
                        Tied to proprietary cloud model routing with no option to supply your enterprise key directly.
                      </div>
                    </td>
                  </tr>

                  <tr>
                    <td><strong>Failure Bundle Quality</strong></td>
                    <td className="landing__col-highlight">
                      <span className="landing__check">
                        <IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} />
                        One-Snapshot Guarantee
                      </span>
                      <div className="landing__table-desc">
                        Exactly 1 snapshotId with video, screenshot, DOM excerpt, root cause, and suggested fix. Never stitches across runs.
                      </div>
                    </td>
                    <td>
                      <span className="landing__cross">
                        <IconX className="w-4 h-4 text-rose-500 inline mr-1" stroke={2} />
                        Scattered Context
                      </span>
                      <div className="landing__table-desc">
                        Mixed run logs and unstructured trace files that confuse coding agents and cause wrong fixes.
                      </div>
                    </td>
                  </tr>

                  <tr>
                    <td><strong>Coding Agent Integration</strong></td>
                    <td className="landing__col-highlight">
                      <span className="landing__check">
                        <IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} />
                        Native MCP (9 Tools) &amp; Exit Codes
                      </span>
                      <div className="landing__table-desc">
                        First-class Model Context Protocol for Claude Code, Cursor, Windsurf, plus 15 documented POSIX exit codes for CI.
                      </div>
                    </td>
                    <td>
                      <span className="landing__cross">
                        <IconX className="w-4 h-4 text-rose-500 inline mr-1" stroke={2} />
                        Proprietary Plugins
                      </span>
                      <div className="landing__table-desc">
                        Requires proprietary IDE extensions and closed-source chat interfaces.
                      </div>
                    </td>
                  </tr>

                  <tr>
                    <td><strong>Deployment &amp; Privacy</strong></td>
                    <td className="landing__col-highlight">
                      <span className="landing__check">
                        <IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} />
                        Open Source &amp; Self-Hostable
                      </span>
                      <div className="landing__table-desc">
                        Apache-2.0 licensed. 1-command Docker Compose stack (Server, Postgres, Redis, MinIO) with strict tenant isolation.
                      </div>
                    </td>
                    <td>
                      <span className="landing__cross">
                        <IconX className="w-4 h-4 text-rose-500 inline mr-1" stroke={2} />
                        Closed Cloud SaaS
                      </span>
                      <div className="landing__table-desc">
                        Your application URLs and test data must transit through third-party cloud infrastructure.
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------- Core Features (Asymmetrical Bento) */}
      <section id="features" className="landing__section max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="landing__section-header">
          <div className="landing__tag">CAPABILITIES</div>
          <h2>Everything needed to verify agent-generated software</h2>
          <p className="muted">
            Built from scratch to turn &quot;does this actually work?&quot; into an immutable, machine-readable verdict.
          </p>
        </div>

        {/* Asymmetrical Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-12">
          {/* Tile 1 (Span 8 cols): Real Product, Never a Simulation */}
          <div className="md:col-span-8 double-bezel group">
            <div className="double-bezel__inner p-8 flex flex-col justify-between h-full">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-blue-500/10 border border-blue-500/20 text-blue-400 flex items-center justify-center mb-6 shadow-[0_0_25px_rgba(59,130,246,0.2)] group-hover:scale-105 transition-transform duration-500">
                  <IconBrowserCheck className="w-6 h-6" stroke={1.5} />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-white mb-3">
                  1. Real Product, Never a Simulation
                </h3>
                <p className="text-slate-300 leading-relaxed mb-6 max-w-2xl text-sm sm:text-base">
                  Every frontend step drives real Chromium against live DOM: real clicks, real navigation,
                  and real rendering. Backend steps perform genuine HTTP calls. A pass means a customer
                  could complete the flow.
                </p>
              </div>
              <div className="bg-[#05070c] rounded-xl p-3.5 border border-white/[0.08] font-mono text-xs text-slate-300 flex items-center gap-2 shadow-inner">
                <IconTerminal2 className="w-4 h-4 text-emerald-400 shrink-0" stroke={1.5} />
                <span>playwright: click(&apos;button#checkout-btn&apos;) → status 200 [ok]</span>
              </div>
            </div>
          </div>

          {/* Tile 2 (Span 4 cols): Auto-Heal without False Greens */}
          <div className="md:col-span-4 double-bezel group">
            <div className="double-bezel__inner p-8 flex flex-col justify-between h-full">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-center justify-center mb-6 shadow-[0_0_25px_rgba(16,185,129,0.2)] group-hover:scale-105 transition-transform duration-500">
                  <IconShieldCheck className="w-6 h-6" stroke={1.5} />
                </div>
                <h3 className="text-xl font-bold tracking-tight text-white mb-3">
                  2. Auto-Heal Integrity
                </h3>
                <p className="text-slate-300 leading-relaxed text-sm">
                  When an element or button is renamed, Justloop updates the locator and replays. But when
                  an assertion fails, the healer strictly refuses to rewrite expected output, preventing false-green rubber stamping.
                </p>
              </div>
              <div className="mt-4 pt-3 border-t border-white/[0.06] flex items-center justify-between text-xs text-emerald-400 font-mono">
                <span>assertion-lock: active</span>
                <span>no-fluff</span>
              </div>
            </div>
          </div>

          {/* Tile 3 (Span 4 cols): Model Context Protocol (MCP) */}
          <div className="md:col-span-4 double-bezel group">
            <div className="double-bezel__inner p-8 flex flex-col justify-between h-full">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-sky-500/10 border border-sky-500/20 text-sky-400 flex items-center justify-center mb-6 shadow-[0_0_25px_rgba(56,189,248,0.2)] group-hover:scale-105 transition-transform duration-500">
                  <IconPlugConnected className="w-6 h-6" stroke={1.5} />
                </div>
                <h3 className="text-xl font-bold tracking-tight text-white mb-3">
                  3. Model Context Protocol
                </h3>
                <p className="text-slate-300 leading-relaxed text-sm">
                  9 native MCP tools out of the box. Claude Code, Cursor, and Codex can list tests, run
                  verifications, read failure bundles, and rerun fixes natively without leaving their terminal workflow.
                </p>
              </div>
              <div className="mt-4 pt-3 border-t border-white/[0.06] text-xs font-mono text-sky-300">
                mcp://justloop-stdio
              </div>
            </div>
          </div>

          {/* Tile 4 (Span 8 cols): One Self-Consistent Failure Bundle */}
          <div className="md:col-span-8 double-bezel group">
            <div className="double-bezel__inner p-8 flex flex-col justify-between h-full">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center justify-center mb-6 shadow-[0_0_25px_rgba(245,158,11,0.2)] group-hover:scale-105 transition-transform duration-500">
                  <IconPackage className="w-6 h-6" stroke={1.5} />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-white mb-3">
                  4. One Self-Consistent Failure Bundle
                </h3>
                <p className="text-slate-300 leading-relaxed mb-6 max-w-2xl text-sm sm:text-base">
                  When a step fails, you get one unified object: failing step, neighbouring context,
                  screenshot, DOM snapshot, session video, and root cause diagnosis. The server strictly
                  refuses to stitch artifacts across runs.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 text-xs font-mono">
                <span className="px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-slate-300 flex items-center gap-1.5">
                  <IconPhoto className="w-3.5 h-3.5 text-blue-400" stroke={1.5} /> screenshot.png
                </span>
                <span className="px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-slate-300 flex items-center gap-1.5">
                  <IconCode className="w-3.5 h-3.5 text-blue-400" stroke={1.5} /> dom.html
                </span>
                <span className="px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-slate-300 flex items-center gap-1.5">
                  <IconPackage className="w-3.5 h-3.5 text-blue-400" stroke={1.5} /> video.webm
                </span>
                <span className="px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-semibold">
                  snapshotId: snp_7823f
                </span>
              </div>
            </div>
          </div>

          {/* Tile 5 (Span 6 cols): Reverse WebSocket Tunnel */}
          <div className="md:col-span-6 double-bezel group">
            <div className="double-bezel__inner p-8 flex flex-col justify-between h-full">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-purple-500/10 border border-purple-500/20 text-purple-400 flex items-center justify-center mb-6 shadow-[0_0_25px_rgba(168,85,247,0.2)] group-hover:scale-105 transition-transform duration-500">
                  <IconNetwork className="w-6 h-6" stroke={1.5} />
                </div>
                <h3 className="text-xl font-bold tracking-tight text-white mb-3">
                  5. Reverse WebSocket Tunnel
                </h3>
                <p className="text-slate-300 leading-relaxed text-sm mb-4">
                  Testing an app on <code className="text-purple-300 bg-purple-500/10 px-1.5 py-0.5 rounded font-mono">localhost:3000</code>?{' '}
                  <code className="text-purple-300 bg-purple-500/10 px-1.5 py-0.5 rounded font-mono">justloop tunnel start</code> creates
                  a secure reverse tunnel so cloud or containerized runners can drive your local dev machine.
                </p>
              </div>
              <div className="font-mono text-xs text-purple-300 bg-purple-500/[0.05] p-2.5 rounded-lg border border-purple-500/10">
                ws://verifyloop-edge:8443/tunnel/tun_live
              </div>
            </div>
          </div>

          {/* Tile 6 (Span 6 cols): AI Test Generator */}
          <div className="md:col-span-6 double-bezel group">
            <div className="double-bezel__inner p-8 flex flex-col justify-between h-full">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 flex items-center justify-center mb-6 shadow-[0_0_25px_rgba(99,102,241,0.2)] group-hover:scale-105 transition-transform duration-500">
                  <IconBrain className="w-6 h-6" stroke={1.5} />
                </div>
                <h3 className="text-xl font-bold tracking-tight text-white mb-3">
                  6. AI Test Generator (BYOK &amp; Hosted)
                </h3>
                <p className="text-slate-300 leading-relaxed text-sm mb-4">
                  Draft comprehensive test plans from PRD documents or user stories in seconds. Use your own
                  API keys (OpenAI, Anthropic, Gemini) with zero markup, or use our affordable hosted models.
                </p>
              </div>
              <div className="font-mono text-xs text-indigo-300 bg-indigo-500/[0.05] p-2.5 rounded-lg border border-indigo-500/10">
                model_route: BYOK_passthrough (no key retention)
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --------------------------------------------------------- How It Works */}
      <section id="how-it-works" className="landing__section">
        <div className="landing__section-header">
          <div className="landing__tag">THE VERIFICATION LOOP</div>
          <h2>From plain-language plan to compound coverage</h2>
          <p className="muted">Five predictable steps that humans or autonomous agents can drive.</p>
        </div>

        <div className="landing__steps">
          {/* Step 1 */}
          <div className="landing__step">
            <div className="landing__step-num">1</div>
            <div className="landing__step-content">
              <h3>Describe in Plain English</h3>
              <p>
                Write checklist steps anyone can read. No complex test frameworks or brittle selectors:
              </p>
              <pre className="landing__code">
{`justloop test create --name "Checkout succeeds" \\
  --step "navigation: Navigate to /cart" \\
  --step "action: Click the Checkout button" \\
  --step 'assertion: Verify the page says "Order Confirmed"'`}
              </pre>
            </div>
          </div>

          {/* Step 2 */}
          <div className="landing__step">
            <div className="landing__step-num">2</div>
            <div className="landing__step-content">
              <h3>Execute on Real Chromium</h3>
              <p>
                Justloop opens your app and executes every step with full network and console monitoring:
              </p>
              <pre className="landing__code">
{`justloop test run tst_7d5a7257 --wait`}
              </pre>
              <p className="landing__code-caption">
                Exit code <code className="text-blue-300 font-mono">0</code> means passed; exit code <code className="text-rose-300 font-mono">1</code> means failed product defect.
              </p>
            </div>
          </div>

          {/* Step 3 */}
          <div className="landing__step">
            <div className="landing__step-num">3</div>
            <div className="landing__step-content">
              <h3>Receive One Self-Consistent Bundle</h3>
              <p>
                If anything breaks, get a complete forensic snapshot under a single snapshotId:
              </p>

              <div className="landing__bundle-preview">
                <div className="landing__bundle-header">
                  <span className="font-semibold text-rose-400 bg-rose-500/10 border border-rose-500/20 px-2 py-0.5 rounded text-[10px]">
                    ASSERTION FAILED
                  </span>
                  <span>snapshot run_23f68068</span>
                </div>
                <div className="landing__bundle-body">
                  <p><strong>Failing Step:</strong> 2. Verify the page says &quot;Order Confirmed&quot;</p>
                  <p><strong>Root Cause:</strong> Expected page to contain &quot;Order Confirmed&quot;, but found &quot;Payment Declined&quot;.</p>
                  <p><strong>Artifacts:</strong> screenshot.png · session.webm · dom.html</p>
                </div>
              </div>
            </div>
          </div>

          {/* Step 4 */}
          <div className="landing__step">
            <div className="landing__step-num">4</div>
            <div className="landing__step-content">
              <h3>Agent Fixes the Root Cause</h3>
              <p>
                Your coding agent (Claude Code / Cursor) reads the exact failure cause, reviews the DOM snapshot,
                patches the bug, and triggers a rerun.
              </p>
            </div>
          </div>

          {/* Step 5 */}
          <div className="landing__step">
            <div className="landing__step-num">5</div>
            <div className="landing__step-content">
              <h3>Rerun &amp; Bank Coverage</h3>
              <p>
                The passing test joins your durable suite. Pass rates climb and regression coverage compounds
                forever instead of vanishing with the chat session.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------- Pricing */}
      <section id="pricing" className="landing__section">
        <div className="landing__section-header">
          <div className="landing__tag">PRICING PREVIEW</div>
          <h2>Plans &amp; rates for preview</h2>
          <p className="muted">
            The plans and prices below are preview information only. Paid billing and credit top-ups are not available yet, and no payment can be made.
          </p>
        </div>

        <div className="landing__pricing-grid">
          {/* Free Tier */}
          <div className="landing__price-card">
            <div className="landing__price-tier">Free</div>
            <div className="landing__price-amount">$0<span>/mo</span></div>
            <p className="landing__price-sub">150 credits included monthly</p>
            <ul className="landing__price-features">
              <li><IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} /> <strong>~300 runs / month</strong></li>
              <li><IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} /> Full verification loop</li>
              <li><IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} /> Real Chromium &amp; HTTP drivers</li>
              <li><IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} /> 1 Environment &amp; 30d history</li>
              <li><IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} /> Native MCP server (9 tools)</li>
              <li><IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} /> Full REST API &amp; CLI access</li>
            </ul>
            <Link to="/login" className="btn-island-secondary !w-full !justify-center mt-auto">
              Get Started Free
            </Link>
          </div>

          {/* Starter Tier */}
          <div className="landing__price-card">
            <div className="landing__price-tier">Starter</div>
            <div className="landing__price-amount">$19<span>/mo</span></div>
            <p className="landing__price-sub">400 credits included monthly</p>
            <ul className="landing__price-features">
              <li><IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} /> <strong>~800 runs / month</strong></li>
              <li><IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} /> All Free features</li>
              <li><IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} /> Auto-heal of stale locators</li>
              <li><IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} /> Scheduled cron runs</li>
              <li><IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} /> Test suites &amp; batch execution</li>
              <li><IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} /> Email failure notifications</li>
            </ul>
            <Link to="/login" className="btn-island-secondary !w-full !justify-center mt-auto">
              Starter Plan (Preview)
            </Link>
          </div>

          {/* Standard Tier (Featured) */}
          <div className="landing__price-card landing__price-card--featured">
            <span className="landing__card-badge">POPULAR</span>
            <div className="landing__price-tier text-blue-300">Standard</div>
            <div className="landing__price-amount">$39<span>/mo</span></div>
            <p className="landing__price-sub">800 credits included monthly</p>
            <ul className="landing__price-features">
              <li><IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} /> <strong>~1,600 runs / month</strong></li>
              <li><IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} /> 3 Environments with credentials</li>
              <li><IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} /> 90 days run history</li>
              <li><IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} /> Reverse WebSocket tunnel</li>
              <li><IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} /> Flakiness stability scoring</li>
              <li><IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} /> Run regression diffing</li>
            </ul>
            <Link to="/login" className="btn-island-primary !w-full mt-auto">
              <span>Standard Plan (Preview)</span>
              <span className="btn-island-icon">
                <IconArrowRight className="w-3.5 h-3.5" stroke={2} />
              </span>
            </Link>
          </div>

          {/* Pro Tier */}
          <div className="landing__price-card">
            <div className="landing__price-tier">Pro</div>
            <div className="landing__price-amount">$69<span>/mo</span></div>
            <p className="landing__price-sub">1,600 credits included monthly</p>
            <ul className="landing__price-features">
              <li><IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} /> <strong>~3,200 runs / month</strong></li>
              <li><IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} /> Unlimited environments</li>
              <li><IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} /> Unlimited run history</li>
              <li><IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} /> Concurrent browser runners</li>
              <li><IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} /> Priority test queue</li>
              <li><IconCheck className="w-4 h-4 text-emerald-400 inline mr-1" stroke={2} /> High-frequency schedules</li>
            </ul>
            <Link to="/login" className="btn-island-secondary !w-full !justify-center mt-auto">
              Pro Plan (Preview)
            </Link>
          </div>
        </div>

        {/* Paid billing availability */}
        <div className="landing__payg-banner">
          <div className="landing__payg-text">
            <h3>Paid billing &amp; credit top-ups are not available yet</h3>
            <p className="muted">
              Credit rates and plan prices on this page are preview information only. No payment can be made.
            </p>
          </div>
        </div>
      </section>

      {/* ----------------------------------------------------------------- FAQ */}
      <section id="faq" className="landing__section landing__faq">
        <div className="landing__section-header">
          <div className="landing__tag">FREQUENTLY ASKED QUESTIONS</div>
          <h2>Clear answers for engineers and teams</h2>
        </div>

        <div className="landing__faq-list">
          {[
            {
              q: 'What does Justloop actually run?',
              a: 'Your real product. For frontend tests, a real Chromium against a live page: real clicks, real navigation, and real rendering. For backend tests, genuine HTTP calls. Nothing is mocked or stubbed. A pass means a customer could complete that flow.',
            },
            {
              q: 'Will auto-heal just edit my failing tests until they pass?',
              a: 'No, and this is deliberate. When a button or locator changes, Justloop repairs the locator. When an assertion fails, the healer strictly refuses to touch it. Rewriting what a test expects would turn assertions into rubber stamps, hiding genuine regressions.',
            },
            {
              q: 'Do you use LLMs during test execution?',
              a: 'Never. Execution is 100% deterministic and heuristic. Running the same test plan against the same app state always yields the exact same execution. LLMs are only used optionally when you ask to draft new test plans from PRD documents.',
            },
            {
              q: 'Can I bring my own LLM API keys (BYOK)?',
              a: 'Yes. For AI test generation, you can supply your own Anthropic, OpenAI, or Gemini key per request. Your key is never persisted or logged. Alternatively, operators can configure a hosted model in the Super Admin settings.',
            },
            {
              q: 'How does it integrate with Claude Code, Cursor, and Codex?',
              a: 'Justloop provides a native Model Context Protocol (MCP) server with 9 tools. Agents can lint plans offline, execute tests, read failure bundles, and trigger reruns without leaving the IDE or chat terminal.',
            },
            {
              q: 'Can I test apps running on my local machine (localhost)?',
              a: 'Yes. Run `justloop tunnel start --port 3000` to establish a secure reverse WebSocket tunnel. The browser runner can drive your laptop app as you build it.',
            },
            {
              q: 'Can I self-host Justloop on my own infrastructure?',
              a: 'Yes. Justloop is 100% open-source under Apache-2.0. We provide pre-built Docker containers and Docker Compose stacks (Server, PostgreSQL, Redis, MinIO S3) ready for single-node or cloud deployments.',
            },
          ].map((item, idx) => (
            <div
              key={item.q}
              className={`landing__faq-item${openFaq === idx ? ' is-open' : ''}`}
              onClick={() => toggleFaq(idx)}
            >
              <div className="landing__faq-q">
                <span>{item.q}</span>
                <span className="landing__faq-icon">
                  {openFaq === idx ? (
                    <IconChevronUp className="w-4 h-4 text-slate-400" stroke={1.5} />
                  ) : (
                    <IconChevronDown className="w-4 h-4 text-slate-400" stroke={1.5} />
                  )}
                </span>
              </div>
              {openFaq === idx && <div className="landing__faq-a">{item.a}</div>}
            </div>
          ))}
        </div>
      </section>

      {/* ------------------------------------------------------------- Bottom CTA */}
      <section className="landing__bottom-cta">
        <h2>Start verifying your agentic software today</h2>
        <p className="muted">
          Stop wondering if your AI coding agent broke the product. Run real tests on real browsers with immutable failure bundles.
        </p>
        <div className="landing__cta-group">
          <Link to="/login" className="btn-island-primary group">
            <span>Create Free Account (150 Credits)</span>
            <span className="btn-island-icon">
              <IconArrowRight className="w-4 h-4" stroke={2} />
            </span>
          </Link>
          <a
            href="https://github.com/alifmaheaven/Justloop"
            target="_blank"
            rel="noreferrer"
            className="btn-island-secondary"
          >
            <IconBrandGithub className="w-4 h-4 text-slate-200" stroke={1.5} />
            <span>Star on GitHub</span>
          </a>
        </div>
      </section>

      {/* ---------------------------------------------------------------- Footer */}
      <footer className="landing__footer">
        <div className="landing__footer-inner">
          <div className="landing__footer-brand">
            <div className="landing__logo">
              <span className="landing__logo-mark">J</span>
              <span className="landing__logo-text">Justloop</span>
            </div>
            <p className="text-xs text-slate-400 mt-2 leading-relaxed">
              The open-source verification layer for coding agents. Real Chromium, zero flakiness, one self-consistent failure bundle.
            </p>
            <p className="text-[11px] text-slate-500 mt-3 font-mono">
              Licensed under Apache-2.0. All trademarks belong to their respective owners.
            </p>
          </div>

          <div className="landing__footer-col">
            <h4>Product</h4>
            <a href="#features">Features</a>
            <a href="#comparison">vs TestSprite</a>
            <a href="#how-it-works">How It Works</a>
            <a href="#pricing">Pricing</a>
            <Link to="/login">Dashboard Sign In</Link>
          </div>

          <div className="landing__footer-col">
            <h4>Developers</h4>
            <Link to="/docs">CLI &amp; Setup Guide</Link>
            <Link to="/docs">MCP Server Config</Link>
            <Link to="/docs">Reverse Tunnel Guide</Link>
            <a href="https://www.npmjs.com/package/justloop" target="_blank" rel="noreferrer">
              npm: justloop
            </a>
          </div>

          <div className="landing__footer-col">
            <h4>Legal</h4>
            <a href="#faq">Terms of Service</a>
            <a href="#faq">Privacy Policy (GDPR)</a>
            <a href="#faq">Data Processing Addendum</a>
            <a href="#faq">Acceptable Use Policy</a>
            <a href="#faq">Security Disclosure</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
